
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";

interface Plan {
  id: string;
  name: string;
  max_organizations: number;
  max_workspaces: number;
  max_boards_per_workspace: number;
  max_pomodoros_per_workspace: number;
  can_add_collaborators: boolean;
  can_share_with_external_users: boolean;
}

interface PlanDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  plan: Plan | null;
  onSuccess: () => void;
}

const planSchema = z.object({
  name: z.string().min(1, "El nombre es requerido"),
  max_organizations: z.coerce.number().min(1, "Debe ser al menos 1"),
  max_workspaces: z.coerce.number().min(1, "Debe ser al menos 1"),
  max_boards_per_workspace: z.coerce.number().min(1, "Debe ser al menos 1"),
  max_pomodoros_per_workspace: z.coerce.number().min(1, "Debe ser al menos 1"),
  can_add_collaborators: z.boolean(),
  can_share_with_external_users: z.boolean(),
});

type PlanFormValues = z.infer<typeof planSchema>;

export const PlanDialog = ({
  open,
  onOpenChange,
  plan,
  onSuccess,
}: PlanDialogProps) => {
  const form = useForm<PlanFormValues>({
    resolver: zodResolver(planSchema),
    defaultValues: plan || {
      name: "",
      max_organizations: 1,
      max_workspaces: 1,
      max_boards_per_workspace: 1,
      max_pomodoros_per_workspace: 1,
      can_add_collaborators: false,
      can_share_with_external_users: false,
    },
  });

  const onSubmit = async (values: PlanFormValues) => {
    try {
      if (plan) {
        const { error } = await supabase
          .from("plans")
          .update({
            ...values,
            updated_at: new Date().toISOString(),
          })
          .eq("id", plan.id);

        if (error) throw error;
        toast({
          title: "Éxito",
          description: "Plan actualizado correctamente",
        });
      } else {
        const { error } = await supabase.from("plans").insert({
          ...values,
          is_active: true,
          name: values.name, // Aseguramos que name está presente
        });
        if (error) throw error;
        toast({
          title: "Éxito",
          description: "Plan creado correctamente",
        });
      }
      onSuccess();
    } catch (error) {
      toast({
        title: "Error",
        description: "Hubo un error al guardar el plan",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {plan ? "Editar Plan" : "Crear Nuevo Plan"}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nombre</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="max_organizations"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Máximo de Organizaciones</FormLabel>
                  <FormControl>
                    <Input type="number" {...field} />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="max_workspaces"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Máximo de Workspaces</FormLabel>
                  <FormControl>
                    <Input type="number" {...field} />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="max_boards_per_workspace"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Máximo de Boards por Workspace</FormLabel>
                  <FormControl>
                    <Input type="number" {...field} />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="max_pomodoros_per_workspace"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Máximo de Pomodoros por Workspace</FormLabel>
                  <FormControl>
                    <Input type="number" {...field} />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="can_add_collaborators"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between">
                  <FormLabel>Puede Agregar Colaboradores</FormLabel>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="can_share_with_external_users"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between">
                  <FormLabel>Puede Compartir con Usuarios Externos</FormLabel>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <Button type="submit" className="w-full">
              {plan ? "Actualizar Plan" : "Crear Plan"}
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
